import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Link, useParams } from "@tanstack/react-router";
import { FileX, SearchX } from "lucide-react";
import type { ReactNode } from "react";
import { fmtUsd, harnessLabel } from "../aggregate";
import { HarnessDot } from "../components/spend-summary";
import { TranscriptMessageItem, timeLabel } from "../components/transcript-message";
import { useLedgerSessions, useLedgerTranscript } from "../hooks";

const BACK_LINK =
	"rounded-sm outline-none hover:text-foreground focus-visible:text-foreground focus-visible:underline";

function Meta({ children }: { children: ReactNode }) {
	return <span className="inline-flex min-w-0 items-center gap-1.5 break-all">{children}</span>;
}

/**
 * One ledger session as a chat: header from the cached session list, messages
 * from the paginated transcript endpoint. Browser back returns to /ledger with
 * its filters, sort and page, since those live in the URL.
 */
export function SessionPage() {
	const { id } = useParams({ strict: false }) as { id: string };
	const sessionsQuery = useLedgerSessions();
	const session = sessionsQuery.data?.find((s) => s.id === id);
	// Unknown ids are never in the list, so the list alone settles not-found;
	// the client error carries no status to tell a 404 apart.
	const notFound = sessionsQuery.isSuccess && !session;
	const transcript = useLedgerTranscript(id, { enabled: !notFound });

	const messages = transcript.data?.pages.flatMap((page) => page.items) ?? [];
	const total = transcript.data?.pages[0]?.total ?? 0;

	const back = (
		<Link to="/ledger" className={BACK_LINK}>
			← Ledger
		</Link>
	);

	if (notFound) {
		return (
			<>
				<PageHeader eyebrow={back} title="Session not found" />
				<PageBody className="px-6 py-5">
					<EmptyState
						icon={SearchX}
						title="Session not found"
						body={`No ledger session has the id ${id}.`}
						primary={
							<Button variant="secondary" size="sm" asChild>
								<Link to="/ledger">Back to the ledger</Link>
							</Button>
						}
					/>
				</PageBody>
			</>
		);
	}

	const subtitle = session ? (
		<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
			<Meta>{session.project}</Meta>
			<Meta>
				<HarnessDot harness={session.harness} />
				{harnessLabel(session.harness)}
			</Meta>
			<Meta>{session.host}</Meta>
			<Meta>
				<span className="tabular-nums">
					{timeLabel(session.first)} – {timeLabel(session.last)}
				</span>
			</Meta>
			<Meta>
				<span className="font-medium tabular-nums text-foreground">{fmtUsd(session.cost)}</span>
			</Meta>
		</span>
	) : sessionsQuery.isLoading ? (
		"Loading session…"
	) : (
		<span className="break-all font-mono">{id}</span>
	);

	return (
		<>
			<PageHeader
				eyebrow={back}
				title={session ? (session.title ?? "(untitled)") : "Session"}
				subtitle={subtitle}
			/>
			<PageBody className="px-6 py-5">
				<div className="mx-auto max-w-3xl min-w-0">
					{transcript.isPending ? (
						<p className="text-body-sm text-ink-subtle">Loading transcript…</p>
					) : transcript.isError ? (
						<EmptyState title="Could not load the transcript" body={transcript.error.message} />
					) : total === 0 ? (
						<EmptyState
							icon={FileX}
							title="Transcript not available"
							body="The raw objects for this session are not in the bucket, or could not be read."
						/>
					) : (
						<>
							<ol aria-label="Transcript" className="space-y-5">
								{messages.map((message, i) => (
									// Messages have no id; offset pages never overlap, so the index is stable.
									// biome-ignore lint/suspicious/noArrayIndexKey: append-only list.
									<TranscriptMessageItem key={i} message={message} />
								))}
							</ol>
							<div className="mt-6 flex flex-wrap items-center gap-3 text-meta text-ink-subtle">
								<span className="tabular-nums">
									Showing {messages.length.toLocaleString("en-US")} of{" "}
									{total.toLocaleString("en-US")}
								</span>
								{transcript.hasNextPage ? (
									<Button
										variant="secondary"
										size="sm"
										onClick={() => transcript.fetchNextPage()}
										disabled={transcript.isFetchingNextPage}
									>
										{transcript.isFetchingNextPage ? "Loading…" : "Load more"}
									</Button>
								) : null}
							</div>
						</>
					)}
				</div>
			</PageBody>
		</>
	);
}
